<template>
  <div>
    <QuickstartCard
      v-if="$route.query.quickstart"
      data-intro="Let's get all of your agents loaded into Humaniz so they can take advantage of free training and setup pipeline reviews with you or your team."
    >
      <template #header>👋 Invite your agent roster</template>
      <template #body>
        Invite your agent roster to Humaniz. Once your agents are added, you can schedule pipeline reviews with them,
        assign them tasks, and give them access to training.
      </template>
    </QuickstartCard>

    <div v-if="!loadingSpinnerStore.loadingSpinner" class="flex flex-col gap-5">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div class="space-y-3">
          <p class="max-w-2xl text-sm text-slate-500">
            Review your active roster, keep pipeline reviewer ownership clear, and spot teammates who need follow-up.
          </p>

          <div class="flex flex-wrap gap-2">
            <Badge variant="gray">{{ totalActive }} active</Badge>
            <Badge variant="gray">{{ totalInactive }} inactive</Badge>
            <Badge variant="gray">{{ total }} total</Badge>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <UiButton variant="outline" size="sm" @click="showMessageTeam = true">
            Message team
            <MessageCircle class="h-4 w-4" />
          </UiButton>

          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <UiButton variant="outline" size="sm">
                More
                <MoreHorizontal class="h-4 w-4" />
              </UiButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              <DropdownMenuItem @select="showImportTeam = true">
                <Upload class="h-4 w-4" />
                Import teammates
              </DropdownMenuItem>
              <DropdownMenuItem @select="exportContacts">
                <Download class="h-4 w-4" />
                Export teammates
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <UiButton as-child size="sm">
            <router-link to="/team/new">
              New
              <Plus class="h-4 w-4" />
            </router-link>
          </UiButton>
        </div>
      </div>

      <DataTable title="Team roster" :summary="filterSummary" :is-empty="rows.length === 0">
        <template #toolbar>
          <TeamListFilters
            :filters="filters"
            :eligible-reviewers="eligibleReviewers"
            @update-filter="handleNewFilter"
          />
        </template>

        <template #empty>
          <DataTableEmpty :title="emptyStateTitle" :body="emptyStateBody">
            <template #icon>
              <Users class="h-5 w-5" />
            </template>
          </DataTableEmpty>
        </template>

        <template #body>
          <div
            class="hidden border-b border-gray-200 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 lg:grid lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)_minmax(0,1.35fr)_minmax(0,1.4fr)_minmax(0,0.85fr)] lg:gap-4"
          >
            <button
              class="flex items-center gap-1 text-left text-gray-500 transition-colors hover:text-gray-900"
              @click="handleNewSort(nextSort('name'))"
            >
              Teammate
              <ArrowUpDown class="h-3.5 w-3.5" :class="sortIconClass('name')" />
            </button>
            <span>Phone</span>
            <button
              class="flex items-center gap-1 text-left text-gray-500 transition-colors hover:text-gray-900"
              @click="handleNewSort(nextSort('pipelinereviewer'))"
            >
              Pipeline review
              <ArrowUpDown class="h-3.5 w-3.5" :class="sortIconClass('pipelinereviewer')" />
            </button>
            <button
              class="flex items-center gap-1 text-left text-gray-500 transition-colors hover:text-gray-900"
              @click="handleNewSort(nextSort('mostrecentpipelinereviewdate'))"
            >
              Recent review
              <ArrowUpDown class="h-3.5 w-3.5" :class="sortIconClass('mostrecentpipelinereviewdate')" />
            </button>
            <button
              class="flex items-center gap-1 text-left text-gray-500 transition-colors hover:text-gray-900"
              @click="handleNewSort(nextSort('created'))"
            >
              Added
              <ArrowUpDown class="h-3.5 w-3.5" :class="sortIconClass('created')" />
            </button>
          </div>

          <div v-for="teammate in rows" :key="teammate.id" class="border-b border-gray-200 last:border-b-0">
            <div class="cursor-pointer px-4 py-3 transition-colors hover:bg-slate-50" @click="viewTeammate(teammate)">
              <div
                class="flex flex-col gap-3 lg:grid lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)_minmax(0,1.35fr)_minmax(0,1.4fr)_minmax(0,0.85fr)] lg:gap-4 lg:items-center"
              >
                <div class="min-w-0 space-y-1">
                  <div class="flex flex-wrap items-center gap-2">
                    <p class="text-sm font-medium text-slate-900">{{ teammate.Name }}</p>
                    <Badge
                      :variant="teammate.Status === 'Active' ? 'green' : 'gray'"
                      class="px-1 py-0 text-[10px] font-medium leading-4"
                    >
                      {{ teammate.Status }}
                    </Badge>
                  </div>
                  <p v-if="teammate.Email" class="truncate text-xs text-slate-500">{{ teammate.Email }}</p>
                  <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 lg:hidden">
                    <span v-if="teammate.PhoneNumber">{{ teammate.PhoneNumber }}</span>
                    <span>Added {{ formatStandardDateTime(teammate.Created) }}</span>
                  </div>
                </div>

                <div class="hidden lg:block">
                  <p class="text-sm text-slate-700">{{ teammate.PhoneNumber || '—' }}</p>
                </div>

                <div class="space-y-1">
                  <p class="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 lg:hidden">
                    Pipeline review
                  </p>
                  <div class="flex items-center gap-2">
                    <p class="text-sm text-slate-700">{{ teammate.PipelineReviewer || 'Unassigned' }}</p>
                    <Badge
                      :variant="teammate.PipelineReviewActive ? 'green' : 'gray'"
                      class="px-1 py-0 text-[10px] font-medium leading-4"
                    >
                      {{ teammate.PipelineReviewActive ? 'Active' : 'Inactive' }}
                    </Badge>
                  </div>
                  <p class="text-xs capitalize text-slate-500">
                    {{ teammate.PipelineReviewCadence || 'No cadence set' }}
                  </p>
                </div>

                <div class="space-y-1">
                  <p class="text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 lg:hidden">
                    Recent review
                  </p>
                  <div v-if="teammate.MostRecentPipelineReviewDate" class="flex items-center gap-2">
                    <ScoreIcon :score="teammate.MostRecentPipelineReviewScore || 0" size="sm" />
                    <span class="text-sm text-slate-700">
                      {{ formatStandardDateTime(teammate.MostRecentPipelineReviewDate) }}
                    </span>
                  </div>
                  <p v-else class="text-sm text-slate-500">No reviews yet</p>
                </div>

                <div class="hidden items-center gap-2 lg:flex">
                  <p class="text-sm text-slate-700">{{ formatStandardDateTime(teammate.Created) }}</p>
                  <ChevronRight class="h-4 w-4 shrink-0 text-slate-400" />
                </div>
              </div>
            </div>
          </div>
        </template>

        <template #footer>
          <DataTablePagination
            v-if="rows.length > 0"
            :current-page="currentPage"
            :number-of-pages="numberOfPages"
            :start-entry="startEntry"
            :end-entry="endEntry"
            :total="total"
            @go-to-page="goToPage"
          />
        </template>
      </DataTable>
    </div>

    <LoadingList />

    <TeamMessage
      v-if="showMessageTeam"
      @handle-message-team="showMessageTeam = false"
      @handle-message-team-cancel="showMessageTeam = false"
    />
    <TeamImport v-model:open="showImportTeam" />
  </div>
</template>

<script>
import {
  ArrowUpDown,
  ChevronRight,
  Download,
  MessageCircle,
  MoreHorizontal,
  Plus,
  Upload,
  Users,
} from 'lucide-vue-next';
import { Badge } from '@/components/ui/badge';
import { Button as UiButton } from '@/components/ui/button';
import { DataTable, DataTableEmpty, DataTablePagination } from '@/components/shared/DataTable/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import ScoreIcon from '@/components/shared/pipeline_reviews/ScoreIcon.vue';
import LoadingList from '../shared/LoadingList.vue';
import QuickstartCard from '../shared/QuickstartCard.vue';
import TeamImport from './TeamImport.vue';
import TeamListFilters from './TeamListFilters.vue';
import TeamMessage from './TeamMessage.vue';
import { formatStandardDateTime } from '../../utilities/format.ts';
import RailsUtility from '../../utilities/rails.ts';

export default {
  name: 'TeammateList',
  components: {
    ArrowUpDown,
    Badge,
    ChevronRight,
    DataTable,
    DataTableEmpty,
    DataTablePagination,
    Download,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    LoadingList,
    MessageCircle,
    MoreHorizontal,
    ScoreIcon,
    Plus,
    QuickstartCard,
    TeamImport,
    TeamListFilters,
    TeamMessage,
    UiButton,
    Upload,
    Users,
  },
  setup() {
    const loadingSpinnerStore = useLoadingSpinnerStore();
    return { loadingSpinnerStore };
  },
  data() {
    return {
      contacts: [],
      startEntry: 0,
      endEntry: 0,
      total: 0,
      totalActive: 0,
      totalInactive: 0,
      eligibleReviewers: [],
      showImportTeam: false,
      showMessageTeam: false,
    };
  },
  computed: {
    rows() {
      return this.contacts;
    },
    currentPage() {
      return parseInt(this.$route.query.page || '1', 10);
    },
    numberOfPages() {
      return Math.max(Math.ceil(this.total / 10), 1);
    },
    hasPreviousPage() {
      return this.currentPage > 1;
    },
    hasNextPage() {
      return this.currentPage < this.numberOfPages;
    },
    sort() {
      return {
        key: this.$route.query.sort || 'created',
        order: this.$route.query.order || 'desc',
      };
    },
    filters() {
      return [
        { key: 'status', value: this.$route.query.status || 'All' },
        { key: 'pipeline_reviewer', value: this.$route.query.pipeline_reviewer || 'All' },
      ];
    },
    selectedStatusValue() {
      return this.$route.query.status || 'All';
    },
    selectedReviewerValue() {
      return this.$route.query.pipeline_reviewer || 'All';
    },
    filterSummary() {
      const parts = [];

      if (this.selectedStatusValue === 'All') {
        parts.push(`${this.totalActive} active and ${this.totalInactive} inactive teammates`);
      } else {
        parts.push(`Showing ${this.selectedStatusValue.toLowerCase()} teammates`);
      }

      if (this.selectedReviewerValue !== 'All') {
        parts.push(`Reviewer: ${this.selectedReviewerValue}`);
      }

      return `${parts.join(' • ')}.`;
    },
    emptyStateTitle() {
      if (this.selectedStatusValue === 'Active') return 'No active teammates';
      if (this.selectedStatusValue === 'Inactive') return 'No inactive teammates';
      return 'No teammates found';
    },
    emptyStateBody() {
      if (this.selectedStatusValue === 'Active') {
        return 'There are no active teammates in this view right now. Adjust the filters or import your roster.';
      }

      if (this.selectedStatusValue === 'Inactive') {
        return 'There are no inactive teammates to review right now.';
      }

      return 'Add or import teammates to start assigning tasks, scheduling reviews, and tracking performance.';
    },
  },
  mounted() {
    this.getContacts();
  },
  methods: {
    formatStandardDateTime,
    sortIconClass(key) {
      return this.sort.key === key ? 'text-slate-900' : 'text-slate-300';
    },
    nextSort(key) {
      if (this.sort.key === key) {
        return { key, order: this.sort.order === 'desc' ? 'asc' : 'desc' };
      }

      return { key, order: 'desc' };
    },
    async goToPage(page) {
      if (page < 1 || page > this.numberOfPages) return;

      await this.$router.push({
        path: '/teammates',
        query: {
          ...this.$route.query,
          page,
        },
      });

      await this.getContacts();
    },
    async exportContacts() {
      this.loadingSpinnerStore.showLoadingSpinner();
      const response = await fetch('/api/teammates/download_csv');
      const data = await response.blob();

      const file = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      document.body.appendChild(link);
      link.style.display = 'none';
      link.href = file;
      link.download = 'teammate_export.csv';
      link.click();
      window.URL.revokeObjectURL(file);
      this.loadingSpinnerStore.hideLoadingSpinner();
    },
    async viewTeammate(record) {
      await this.$router.push({ name: 'TeammateDetail', params: { id: record.id } });
    },
    async getContacts() {
      this.loadingSpinnerStore.showLoadingSpinner();

      const response = await fetch(`/api${this.$route.fullPath}`);
      const data = await response.json();

      this.contacts = data.records.map(record => ({
        Name: record.name,
        PhoneNumber: record.phone_number,
        Email: record.email,
        Status: record.teammate_status,
        PipelineReviewActive: record.pipeline_review_contact_configuration?.active ?? false,
        PipelineReviewer: record.pipeline_review_contact_configuration?.reviewer_display_name,
        PipelineReviewCadence: RailsUtility.convertFromEnumFormat(
          record.pipeline_review_contact_configuration?.cadence || ''
        ),
        MostRecentPipelineReviewScore: record.pipe_score || 0,
        MostRecentPipelineReviewDate: record.pipe_created_at,
        Created: record.created_at,
        id: record.id,
      }));
      this.total = data.total;
      this.startEntry = data.start_entry;
      this.endEntry = data.end_entry;
      this.totalActive = data.total_active;
      this.totalInactive = data.total_inactive;
      this.eligibleReviewers = data.eligible_pipeline_reviewer_filters;
      this.loadingSpinnerStore.hideLoadingSpinner();
    },
    async handleNewSort(sort) {
      await this.$router.push({
        path: '/teammates',
        query: {
          sort: sort.key,
          order: sort.order,
          status: this.$route.query.status || 'All',
          pipeline_reviewer: this.$route.query.pipeline_reviewer || 'All',
        },
      });

      await this.getContacts();
    },
    async handleNewFilter(newFilters) {
      await this.$router.push({
        path: '/teammates',
        query: {
          sort: this.sort.key,
          order: this.sort.order,
          status: newFilters.status.value || 'All',
          pipeline_reviewer: newFilters.pipeline_reviewer.value || 'All',
        },
      });

      await this.getContacts();
    },
  },
};
</script>
